import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Upload as UploadIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  TableChart as TableChartIcon,
} from '@mui/icons-material';
import axiosInstance from '../utlis/axiosInstance';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

interface UploadStatus {
  isUploading: boolean;
  isSuccess: boolean;
  isError: boolean;
  message: string;
  fileName?: string;
}

const BulkAddUsers: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
    isUploading: false,
    isSuccess: false,
    isError: false,
    message: '',
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check if file is CSV or XLSX
      const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
      const isXLSX = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                     file.name.endsWith('.xlsx') || 
                     file.type === 'application/vnd.ms-excel' || 
                     file.name.endsWith('.xls');
      
      if (!isCSV && !isXLSX) {
        toast.error('Please select a valid CSV or XLSX file');
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size should be less than 10MB');
        return;
      }

      setSelectedFile(file);
      setUploadStatus({
        isUploading: false,
        isSuccess: false,
        isError: false,
        message: '',
      });
    }
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      // Check if file is CSV or XLSX
      const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
      const isXLSX = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                     file.name.endsWith('.xlsx') || 
                     file.type === 'application/vnd.ms-excel' || 
                     file.name.endsWith('.xls');
      
      if (!isCSV && !isXLSX) {
        toast.error('Please select a valid CSV or XLSX file');
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size should be less than 10MB');
        return;
      }

      setSelectedFile(file);
      setUploadStatus({
        isUploading: false,
        isSuccess: false,
        isError: false,
        message: '',
      });
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadStatus({
      isUploading: false,
      isSuccess: false,
      isError: false,
      message: '',
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const convertXLSXToCSV = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const csvData = XLSX.utils.sheet_to_csv(worksheet);
          
          // Create a new CSV file
          const csvBlob = new Blob([csvData], { type: 'text/csv' });
          const csvFile = new File([csvBlob], file.name.replace(/\.(xlsx|xls)$/i, '.csv'), {
            type: 'text/csv'
          });
          
          resolve(csvFile);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsBinaryString(file);
    });
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast.error('Please select a CSV or XLSX file first');
      return;
    }

    setUploadStatus({
      isUploading: true,
      isSuccess: false,
      isError: false,
      message: 'Processing file...',
    });

    try {
      let fileToUpload = selectedFile;
      
      // Check if file is XLSX and convert to CSV
      const isXLSX = selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                     selectedFile.name.endsWith('.xlsx') || 
                     selectedFile.type === 'application/vnd.ms-excel' || 
                     selectedFile.name.endsWith('.xls');
      
      if (isXLSX) {
        setUploadStatus(prev => ({ ...prev, message: 'Converting XLSX to CSV...' }));
        fileToUpload = await convertXLSXToCSV(selectedFile);
      }

      setUploadStatus(prev => ({ ...prev, message: 'Uploading file...' }));
      
      const formData = new FormData();
      formData.append('file', fileToUpload);

      const response = await axiosInstance.post('/gpstracker/userbulkuploadhandler/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadStatus({
        isUploading: false,
        isSuccess: true,
        isError: false,
        message: 'File uploaded successfully!',
        fileName: selectedFile.name,
      });

      const respData = (response as any)?.data?.responseData;
      const totalProcessed = respData?.total_processed ?? 0;
      const successfullyCreated = respData?.successfully_created ?? 0;
      const failed = respData?.failed ?? 0;

      toast.success(`Processed: ${totalProcessed} | Created: ${successfullyCreated} | Failed: ${failed}`);

      // Reset form after successful upload
      setTimeout(() => {
        handleRemoveFile();
      }, 3000);
    } catch (error: any) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Upload failed. Please try again.';

      setUploadStatus({
        isUploading: false,
        isSuccess: false,
        isError: true,
        message: errorMessage,
        fileName: selectedFile.name,
      });

      toast.error(errorMessage);
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = `Device Name,Unique ID,Phone,Model,Category,Contact,Status
Sample Device 1,DEV001,+1234567890,Sample Model,Car,John Doe,enabled
Sample Device 2,DEV002,+1234567891,Sample Model 2,Truck,Jane Smith,enabled`;

    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_upload_sample.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadBulkTestCSV = () => {
    const bulkTestData = `name,username,email,password,userLimit,deviceLimit,phone,userRole
Yuvraj bulk,yuvrajjwala,kt17@gmail.com,kt17@gmail.com,10,10,8789878812,users`;

    const blob = new Blob([bulkTestData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'BulkTest.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadBulkTestXLSX = () => {
    // Create sample data for users
    const sampleData = [
      ['name', 'username', 'email', 'password', 'userLimit', 'deviceLimit', 'phone', 'userRole'],
      ['Yuvraj bulk', 'yuvrajjwala', 'kt17@gmail.com', 'kt17@gmail.com', '10', '10', '8789878812', 'users'],
      ['John Doe', 'johndoe', 'john.doe@example.com', 'password123', '5', '5', '9876543210', 'users'],
      ['Jane Smith', 'janesmith', 'jane.smith@example.com', 'password456', '8', '8', '9876543211', 'admin']
    ];

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(sampleData);
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
    
    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Download the file
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'BulkTest.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Container
      maxWidth='lg'
      sx={{ py: 4 }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ mb: 2 }}
        >
          <Stack
            direction='row'
            alignItems='center'
            spacing={2}
          >
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant='h4'
              component='h1'
              fontWeight={600}
            >
              Bulk Add Users
            </Typography>
          </Stack>
          <Stack direction='row' spacing={2}>
            <Button
              variant='outlined'
              startIcon={<DownloadIcon />}
              onClick={downloadBulkTestCSV}
              sx={{
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  borderColor: theme.palette.primary.dark,
                },
              }}
            >
              Download Sample CSV
            </Button>
            <Button
              variant='outlined'
              startIcon={<TableChartIcon />}
              onClick={downloadBulkTestXLSX}
              sx={{
                borderColor: theme.palette.success.main,
                color: theme.palette.success.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  borderColor: theme.palette.success.dark,
                },
              }}
            >
              Download Sample XLSX
            </Button>
          </Stack>
        </Stack>
        <Typography
          variant='body1'
          color='text.secondary'
        >
          Upload a CSV or XLSX file to add multiple users at once. XLSX files will be automatically converted to CSV format. Make sure your file follows the required format.
        </Typography>
      </Box>

      <Stack spacing={3}>
        {/* Upload Area */}
        <Card>
          <CardContent>
            <Typography
              variant='h6'
              fontWeight={600}
              sx={{ mb: 3 }}
            >
              Upload CSV or XLSX File
            </Typography>

            {/* File Upload Area */}
            <Box
              sx={{
                border: `2px dashed ${selectedFile ? theme.palette.primary.main : theme.palette.grey[300]}`,
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                backgroundColor: selectedFile ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.grey[100], 0.5),
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleFileDrop}
              onDragOver={handleDragOver}
            >
              <input
                ref={fileInputRef}
                type='file'
                accept='.csv,.xlsx,.xls'
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              {!selectedFile ? (
                <Stack
                  spacing={2}
                  alignItems='center'
                >
                  <CloudUploadIcon
                    sx={{
                      fontSize: 48,
                      color: theme.palette.primary.main,
                    }}
                  />
                  <Box>
                    <Typography
                      variant='h6'
                      fontWeight={600}
                      sx={{ mb: 1 }}
                    >
                      Drop your CSV or XLSX file here
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mb: 2 }}
                    >
                      or click to browse files
                    </Typography>
                    <Button
                      variant='contained'
                      startIcon={<UploadIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      sx={{
                        bgcolor: 'var(--primary-color)',
                        '&:hover': {
                          bgcolor: 'var(--primary-light)',
                        },
                      }}
                    >
                      Choose File
                    </Button>
                  </Box>
                </Stack>
              ) : (
                <Stack
                  spacing={2}
                  alignItems='center'
                >
                  <DescriptionIcon
                    sx={{
                      fontSize: 48,
                      color: theme.palette.success.main,
                    }}
                  />
                  <Box>
                    <Typography
                      variant='h6'
                      fontWeight={600}
                      sx={{ mb: 1 }}
                    >
                      {selectedFile.name}
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mb: 2 }}
                    >
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                    <Stack
                      direction='row'
                      spacing={1}
                    >
                      <Button
                        variant='outlined'
                        startIcon={<DeleteIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile();
                        }}
                        color='error'
                      >
                        Remove
                      </Button>
                      <Button
                        variant='outlined'
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                      >
                        Change File
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              )}
            </Box>

            {/* Upload Status */}
            {uploadStatus.message && (
              <Box sx={{ mt: 3 }}>
                <Alert
                  severity={uploadStatus.isUploading ? 'info' : uploadStatus.isSuccess ? 'success' : uploadStatus.isError ? 'error' : 'info'}
                  icon={
                    uploadStatus.isUploading ? (
                      <CircularProgress size={20} />
                    ) : uploadStatus.isSuccess ? (
                      <CheckCircleIcon />
                    ) : uploadStatus.isError ? (
                      <ErrorIcon />
                    ) : undefined
                  }
                >
                  {uploadStatus.message}
                </Alert>
              </Box>
            )}

            {/* Submit Button */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant='contained'
                size='large'
                onClick={handleSubmit}
                disabled={!selectedFile || uploadStatus.isUploading}
                startIcon={
                  uploadStatus.isUploading ? (
                    <CircularProgress
                      size={20}
                      color='inherit'
                    />
                  ) : (
                    <UploadIcon />
                  )
                }
                sx={{
                  bgcolor: 'var(--primary-color)',
                  '&:hover': {
                    bgcolor: 'var(--primary-light)',
                  },
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                }}
              >
                {uploadStatus.isUploading ? 'Uploading...' : 'Upload File'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
};

export default BulkAddUsers;
